import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from "@angular/core";
import { Router, RouterLink, RouterOutlet } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import {
  KsgAppLayout,
  KsgTopbar,
  KsgThemeToggle,
  KsgSidebarFooterDef,
  KsgSessionTimer,
  KsgLanguageSelector,
  KsgUserPanel,
  ksgRouterActiveUrl,
  type KsgSidebarSection,
} from "@ksg/ui";
import { SessionService } from "../core/session/session.service";
import { AuthService } from "../core/auth/auth.service";
import { DemoNotice } from "../shared/demo/demo-notice";

interface NavEntry {
  key: string;
  icon: string;
  url: string;
  section: "overview" | "management" | "system";
}

const NAV: readonly NavEntry[] = [
  { key: "dashboard", icon: "speed", url: "/", section: "overview" },
  {
    key: "statistics",
    icon: "bar_chart",
    url: "/statistics",
    section: "overview",
  },
  { key: "tenants", icon: "apartment", url: "/tenants", section: "management" },
  { key: "users", icon: "group", url: "/users", section: "management" },
  {
    key: "accountants",
    icon: "work",
    url: "/accountants",
    section: "management",
  },
  { key: "hrStaff", icon: "badge", url: "/hr-staff", section: "management" },
  { key: "plans", icon: "star", url: "/plans", section: "management" },
  { key: "payments", icon: "payments", url: "/payments", section: "management" },
  {
    key: "promoCodes",
    icon: "sell",
    url: "/promo-codes",
    section: "management",
  },
  {
    key: "defaultTemplate",
    icon: "palette",
    url: "/default-template",
    section: "system",
  },
  { key: "support", icon: "flag", url: "/support", section: "system" },
  { key: "settings", icon: "settings", url: "/settings", section: "system" },
] as const;

@Component({
  selector: "app-shell",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    TranslocoPipe,
    KsgAppLayout,
    KsgTopbar,
    KsgThemeToggle,
    KsgSidebarFooterDef,
    KsgSessionTimer,
    KsgLanguageSelector,
    KsgUserPanel,
    DemoNotice,
  ],
  templateUrl: "./app-shell.html",
  styleUrl: "./app-shell.scss",
})
export class AppShell {
  protected readonly session = inject(SessionService);
  protected readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  private readonly rawUrl = ksgRouterActiveUrl();

  private readonly i18nReady = toSignal(
    this.transloco.selectTranslate("menu.dashboard"),
  );

  private static readonly ROOTS = NAV.map((n) => n.url).filter(
    (u) => u !== "/",
  );

  protected readonly activeUrl = computed(() => {
    const u = this.rawUrl().split("?")[0].split("#")[0];
    if (u === "/" || u === "") return "/";
    return AppShell.ROOTS.find((r) => u === r || u.startsWith(r + "/")) ?? u;
  });

  protected readonly sections = computed<KsgSidebarSection[]>(() => {
    this.i18nReady();
    const t = (k: string) => this.transloco.translate(k);
    const item = (n: NavEntry) => ({
      label: t("menu." + n.key),
      icon: n.icon,
      url: n.url,
      command: () => void this.router.navigate([n.url]),
    });
    const inSection = (s: NavEntry["section"]) =>
      NAV.filter((n) => n.section === s).map(item);
    return [
      { items: inSection("overview") },
      { header: t("menu.section.management"), items: inSection("management") },
      { header: t("menu.section.system"), items: inSection("system") },
    ];
  });
}
